import type { ReceiptShift, ReceiptShifts } from './types'
import { placeMatches } from './analytics'
import { firstOfMonth, lastOfMonth, type DayRange } from '@krp/ui-kit'

/** Смена может пересекать месяц; выбор столбца сохраняет месяц уже загруженного отчёта. */
export function limitShiftRange(range: DayRange, month: string): DayRange | null {
  const start = firstOfMonth(month), end = lastOfMonth(month)
  const from = range.from < start ? start : range.from
  const to = range.to > end ? end : range.to
  return from > to ? null : { from, to }
}

export const shiftLocalDay = (at: string, timeZoneId: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timeZoneId, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(at))
  return `${parts.find((part) => part.type === 'year')?.value ?? ''}-${parts.find((part) => part.type === 'month')?.value ?? ''}-${parts.find((part) => part.type === 'day')?.value ?? ''}`
}
export function shiftRange(shift: ReceiptShift, zone: string) {
  const first = shift.firstReceiptAt ?? shift.openedAt
  const last = shift.lastReceiptAt ?? shift.closedAt ?? first
  return first === null || last === null ? null : { from: shiftLocalDay(first, zone), to: shiftLocalDay(last, zone) }
}

/** Смена месяца и её дни (`null` — источник не передал ни времени смены, ни времени чеков). */
export interface MonthShift { shift: ReceiptShift; range: DayRange | null }
/** Смены кассира, задевающие месяц `month` (любой день месяца), по месту продажи — от ранних к поздним. */
export function shiftsOfMonth(data: ReceiptShifts | undefined, month: string, zone: string, place: string): MonthShift[] {
  const start = firstOfMonth(month), end = lastOfMonth(month)
  const began = (shift: ReceiptShift) => shift.openedAt ?? shift.firstReceiptAt ?? ''
  return (data?.items ?? []).filter((shift) => placeMatches(shift, place))
    .map((shift) => ({ shift, range: shiftRange(shift, zone) }))
    .filter(({ range }) => range === null || range.from <= end && range.to >= start)
    .sort((a, b) => began(a.shift).localeCompare(began(b.shift)))
}

/** День месяца, в который кассир выходил на смену: число смен и приход по ним в копейках. */
export interface ShiftDay { day: string; count: number; saleCents: number }
/** По дням месяца: когда кассир выходил на смену. Смена через полночь считается в день её начала (в пределах месяца). */
export function shiftDays(shifts: readonly MonthShift[], month: string): Map<string, ShiftDay> {
  const start = firstOfMonth(month), end = lastOfMonth(month), days = new Map<string, ShiftDay>()
  for (const { shift, range } of shifts) {
    if (range === null) continue
    const day = range.from < start ? start : range.from
    if (day > end) continue
    const value = days.get(day) ?? { day, count: 0, saleCents: 0 }
    value.count += 1
    value.saleCents += Math.round(shift.saleAmount * 100)
    days.set(day, value)
  }
  return days
}
